/* Direct-to-Creditor instruments — debt settlement / swap / finance /
 * refinance / collateral / payoff. Rendered into the binder when a deal has
 * a `creditorDirection`. Props: { d } where d.creditorDirection holds the
 * creditor + target-debt details captured in the workbench.
 */
import React from "react";
import { fmtCurrency, fmtDate } from "../../lib/format";
import { calc, DocFooter, Paper, makeSerial, SignatureLine, findSig } from "./_shared";

const mt = (d) => d.trusteeName || "Thomas Franklin";
const mtTitle = (d) => d.trusteeTitle || "Managing Trustee";

// Inline Trust execution block — Managing Trustee (Thomas Franklin) + Co-Trustee.
// (Notary acknowledgement is rendered on the appended execution page.)
const TrustExecution = ({ d }) => (
  <div style={{ marginTop: 22, borderTop: "1.5px solid #0A0A0A", paddingTop: 14 }}>
    <div style={{ fontSize: "8.5pt", letterSpacing: "0.16em", textTransform: "uppercase", color: "#5C5C5C", marginBottom: 12 }}>
      Executed by TREASURY HOUSE TRUST, by and through its Managing Trustee
    </div>
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 32 }}>
      <div>
        <SignatureLine sig={findSig(d.signatures, "seller") || findSig(d.signatures, "trustee")} defaultName={mt(d)} />
        <div className="sig-line" style={{ marginTop: 4 }}>{mt(d)}, {mtTitle(d)}</div>
      </div>
      <div>
        <SignatureLine sig={findSig(d.signatures, "trustee")} defaultName={d.cotrusteeName} />
        <div className="sig-line" style={{ marginTop: 4 }}>{d.cotrusteeName}, {d.cotrusteeTitle || "Independent Co-Trustee"}</div>
      </div>
    </div>
  </div>
);


const cd = (d) => d.creditorDirection || {};
const noteRef = (d) =>
  makeSerial("THT-NOTE-" + (d.trustName || d.borrowerName || "X").replace(/\s/g, "").slice(0, 6).toUpperCase());

const SectionTitle = ({ children }) => (
  <h3 style={{ fontSize: "11pt", fontWeight: 600, margin: "16px 0 6px", color: "#0A0A0A" }}>{children}</h3>
);
const P = ({ children }) => (
  <p style={{ fontSize: "10pt", lineHeight: 1.6, margin: "0 0 10px", color: "#1A1A1A" }}>{children}</p>
);
const DocHead = ({ tag, title, sub }) => (
  <div style={{ borderBottom: "2px solid #0A0A0A", paddingBottom: 10, marginBottom: 16 }}>
    <div style={{ fontSize: "8.5pt", letterSpacing: "0.2em", textTransform: "uppercase", color: "#5C5C5C" }}>{tag}</div>
    <div style={{ fontFamily: "var(--font-serif-display)", fontSize: "20pt", fontWeight: 600, marginTop: 4 }}>{title}</div>
    {sub && <div style={{ fontSize: "9.5pt", color: "#5C5C5C", marginTop: 2 }}>{sub}</div>}
  </div>
);

// ---- CR-01 — Collateral Assignment & Security Agreement (Pledge of Note) ----
export const DocCR01CollateralAssignment = React.forwardRef(({ d }, ref) => {
  const c = cd(d);
  return (
    <Paper ref={ref} footer={<DocFooter label="Doc CR-01 — Collateral Assignment & Security Agreement (Pledge of Note)" />}>
      <DocHead tag="Secured Financing Instrument · UCC Article 9"
        title="Collateral Assignment & Security Agreement"
        sub={`Pledge of Negotiable Instrument · ${c.purposeLabel || "Collateral"}`} />
      <P>
        This Collateral Assignment &amp; Security Agreement (this &ldquo;Agreement&rdquo;) is made effective
        as of <strong>{fmtDate(c.effectiveDate)}</strong> by and between <strong>{d.trustName || d.borrowerName}</strong>
        (the &ldquo;Pledgor&rdquo;) and <strong>{c.creditorName || "the Secured Party"}</strong>
        {c.creditorAddress ? `, of ${c.creditorAddress}` : ""} (the &ldquo;Secured Party&rdquo; / &ldquo;Creditor&rdquo;).
      </P>
      <SectionTitle>1. Grant of Security Interest</SectionTitle>
      <P>
        As security for the Obligations described in Section 2, the Pledgor hereby pledges, assigns and grants to the
        Secured Party a continuing first-priority security interest in that certain negotiable instrument identified as
        <strong> {noteRef(d)}</strong> in the original face principal amount of <strong>{fmtCurrency(d.faceValue)}</strong>,
        maturing <strong>{fmtDate(d.maturityDate)}</strong>, together with all proceeds, products and accessions thereto
        (collectively, the &ldquo;Collateral&rdquo;).
      </P>
      <SectionTitle>2. Secured Obligations</SectionTitle>
      <P>
        This Agreement secures the <strong>{c.debtTypeLabel || "debt obligation"}</strong>
        {c.accountNumber ? ` (Account/Loan No. ${c.accountNumber})` : ""} owing to the Secured Party,
        having a current outstanding balance of <strong>{fmtCurrency(c.currentBalance)}</strong>
        {c.servicer ? `, serviced by ${c.servicer}` : ""} (the &ldquo;Obligations&rdquo;).
        {c.collateralDescription ? ` Additional collateral description: ${c.collateralDescription}.` : ""}
      </P>
      <SectionTitle>3. Perfection</SectionTitle>
      <P>
        The Secured Party is authorized to file a UCC-1 financing statement (Doc CR-02) and to take possession of the
        Collateral instrument to perfect its security interest under UCC §§ 9-313 and 9-330. The Pledgor shall deliver
        the original instrument, duly endorsed, to the Secured Party or its custodian.
      </P>
      <SectionTitle>4. Representations</SectionTitle>
      <P>
        The Pledgor represents that it is the lawful holder of the Collateral free of competing liens, that the
        instrument is genuine and enforceable, and that it has full authority to pledge the Collateral hereunder.
      </P>
      <SectionTitle>5. Governing Law</SectionTitle>
      <P>This Agreement is governed by the laws of {d.borrowerState || "Tennessee"} and the Uniform Commercial Code.</P>
    </Paper>
  );
});

// ---- CR-02 — UCC-1 Financing Statement (creditor as secured party) ----
export const DocCR02UCC1Creditor = React.forwardRef(({ d }, ref) => {
  const c = cd(d);
  const filingNo = makeSerial("UCC1-" + (c.creditorName || "SP").replace(/\s/g, "").slice(0, 5).toUpperCase());
  return (
    <Paper ref={ref} footer={<DocFooter label="Doc CR-02 — UCC-1 Financing Statement" />}>
      <DocHead tag="UCC Form 1 · National Financing Statement" title="UCC-1 Financing Statement"
        sub={`Filing Ref ${filingNo}`} />
      <table className="legal-table" style={{ width: "100%", fontSize: "10pt", borderCollapse: "collapse" }}>
        <tbody>
          <tr><td style={{ width: "34%", fontWeight: 600, padding: "6px 4px", borderBottom: "1px solid #E5E5DF" }}>1. Debtor (Pledgor)</td>
            <td style={{ padding: "6px 4px", borderBottom: "1px solid #E5E5DF" }}>{d.trustName || d.borrowerName}{d.borrowerAddress ? ` · ${d.borrowerAddress}` : ""}</td></tr>
          <tr><td style={{ fontWeight: 600, padding: "6px 4px", borderBottom: "1px solid #E5E5DF" }}>2. Secured Party (Creditor)</td>
            <td style={{ padding: "6px 4px", borderBottom: "1px solid #E5E5DF" }}>{c.creditorName}{c.creditorAddress ? ` · ${c.creditorAddress}` : ""}</td></tr>
          <tr><td style={{ fontWeight: 600, padding: "6px 4px", borderBottom: "1px solid #E5E5DF", verticalAlign: "top" }}>3. Collateral</td>
            <td style={{ padding: "6px 4px", borderBottom: "1px solid #E5E5DF" }}>
              Negotiable instrument {noteRef(d)}, original face principal {fmtCurrency(d.faceValue)}, maturing {fmtDate(d.maturityDate)},
              together with all proceeds and products thereof, pledged as security under the Collateral Assignment &amp; Security
              Agreement (Doc CR-01).{c.collateralDescription ? ` ${c.collateralDescription}.` : ""}
            </td></tr>
          <tr><td style={{ fontWeight: 600, padding: "6px 4px", borderBottom: "1px solid #E5E5DF" }}>4. Secured Obligation</td>
            <td style={{ padding: "6px 4px", borderBottom: "1px solid #E5E5DF" }}>{c.debtTypeLabel}{c.accountNumber ? ` · Acct ${c.accountNumber}` : ""} · Balance {fmtCurrency(c.currentBalance)}</td></tr>
          <tr><td style={{ fontWeight: 600, padding: "6px 4px" }}>5. Filing Effective Date</td>
            <td style={{ padding: "6px 4px" }}>{fmtDate(c.effectiveDate)}</td></tr>
        </tbody>
      </table>
      <P>
        This financing statement is filed to perfect the security interest granted by the Debtor to the Secured Party in
        the Collateral described above pursuant to UCC Article 9. It shall remain effective for five (5) years from the
        filing date unless continued.
      </P>
    </Paper>
  );
});

// ---- CR-03 — Instruction to Apply Proceeds / Payoff Tender Letter ----
export const DocCR03PayoffTender = React.forwardRef(({ d }, ref) => {
  const c = cd(d);
  return (
    <Paper ref={ref} footer={<DocFooter label="Doc CR-03 — Instruction to Apply Proceeds / Payoff Tender Letter" />}>
      <DocHead tag="Payoff Tender · Direction of Proceeds" title="Instruction to Apply Proceeds"
        sub="Payoff Tender Letter" />
      <P>Date: <strong>{fmtDate(c.effectiveDate)}</strong></P>
      <P>
        To: <strong>{c.creditorName || "Creditor / Servicer"}</strong>{c.servicer ? ` (Servicer: ${c.servicer})` : ""}
        {c.creditorAddress ? `, ${c.creditorAddress}` : ""}
      </P>
      <P>
        Re: Payoff / settlement of <strong>{c.debtTypeLabel || "the existing debt obligation"}</strong>
        {c.accountNumber ? `, Account/Loan No. ${c.accountNumber}` : ""}.
      </P>
      <P>
        {d.trustName || d.borrowerName} (the &ldquo;Holder&rdquo;) hereby tenders, and irrevocably directs the application
        of, the proceeds of negotiable instrument <strong>{noteRef(d)}</strong> (original face principal
        <strong> {fmtCurrency(d.faceValue)}</strong>, maturing {fmtDate(d.maturityDate)}) to the payoff and full
        satisfaction of the above-referenced obligation.
      </P>
      <table className="legal-table" style={{ width: "100%", fontSize: "10pt", margin: "12px 0" }}>
        <tbody>
          <tr><td style={{ width: "55%", fontWeight: 600 }}>Current Outstanding Balance</td><td>{fmtCurrency(c.currentBalance)}</td></tr>
          <tr><td style={{ fontWeight: 600 }}>Payoff / Settlement Amount Tendered</td><td>{fmtCurrency(c.payoffAmount || c.currentBalance)}</td></tr>
          <tr><td style={{ fontWeight: 600 }}>Instrument Face Principal Applied</td><td>{fmtCurrency(d.faceValue)}</td></tr>
          <tr><td style={{ fontWeight: 600 }}>Effective Date</td><td>{fmtDate(c.effectiveDate)}</td></tr>
        </tbody>
      </table>
      <P>
        Upon application of the tendered proceeds, the Creditor shall mark the obligation paid-in-full (or settled as
        agreed), release any related lien, and deliver a satisfaction/release to the Holder within ten (10) business days.
        This tender is made under the Holder&rsquo;s reservation of all rights.
      </P>
    </Paper>
  );
});

// ---- CR-04 — Notice of Tender & Conditional Acceptance for Settlement ----
export const DocCR04NoticeOfTender = React.forwardRef(({ d }, ref) => {
  const c = cd(d);
  const isSwap = c.purpose === "debt_swap";
  return (
    <Paper ref={ref} footer={<DocFooter label="Doc CR-04 — Notice of Tender & Conditional Acceptance" />}>
      <DocHead tag={isSwap ? "Debt Swap · Tender Notice" : "Debt Settlement · Tender Notice"}
        title="Notice of Tender & Conditional Acceptance"
        sub={`for ${isSwap ? "Exchange / Swap" : "Settlement"} of Debt`} />
      <P>Date: <strong>{fmtDate(c.effectiveDate)}</strong></P>
      <P>To: <strong>{c.creditorName || "Creditor"}</strong>{c.creditorAddress ? `, ${c.creditorAddress}` : ""}</P>
      <P>
        {d.trustName || d.borrowerName} (&ldquo;Holder&rdquo;) hereby gives notice of tender of negotiable instrument
        <strong> {noteRef(d)}</strong>, original face principal <strong>{fmtCurrency(d.faceValue)}</strong>, maturing
        {fmtDate(d.maturityDate)}, in {isSwap ? "exchange for and swap of" : "settlement and full satisfaction of"} the
        following obligation:
      </P>
      <table className="legal-table" style={{ width: "100%", fontSize: "10pt", margin: "10px 0" }}>
        <tbody>
          <tr><td style={{ width: "45%", fontWeight: 600 }}>Obligation Type</td><td>{c.debtTypeLabel}</td></tr>
          <tr><td style={{ fontWeight: 600 }}>Account / Loan No.</td><td>{c.accountNumber || "—"}</td></tr>
          <tr><td style={{ fontWeight: 600 }}>Current Balance</td><td>{fmtCurrency(c.currentBalance)}</td></tr>
          <tr><td style={{ fontWeight: 600 }}>{isSwap ? "Swap" : "Settlement"} Amount</td><td>{fmtCurrency(c.payoffAmount || c.currentBalance)}</td></tr>
          <tr><td style={{ fontWeight: 600 }}>Servicer</td><td>{c.servicer || "—"}</td></tr>
        </tbody>
      </table>
      <SectionTitle>Conditional Acceptance</SectionTitle>
      <P>
        The Creditor&rsquo;s acceptance of this tender, negotiation of the instrument, or failure to return the
        instrument with written objection within ten (10) business days shall constitute conditional acceptance and
        accord-and-satisfaction of the obligation in accordance with UCC § 3-311, whereupon the Creditor shall release
        all liens and deliver a satisfaction/release to the Holder.
      </P>
    </Paper>
  );
});

// ---- CR-05 — Assignment of Negotiable Instrument in Satisfaction ----
export const DocCR05AssignmentInSatisfaction = React.forwardRef(({ d }, ref) => {
  const c = cd(d);
  const { maturity } = calc(d);
  return (
    <Paper ref={ref} footer={<DocFooter label="Doc CR-05 — Assignment of Negotiable Instrument in Satisfaction" />}>
      <DocHead tag="Assignment · Accord & Satisfaction" title="Assignment of Negotiable Instrument"
        sub="In Satisfaction of Debt" />
      <P>
        For value received and in <strong>{c.purposeLabel || "settlement"}</strong> of the obligation described below,
        <strong> {d.trustName || d.borrowerName}</strong> (&ldquo;Assignor&rdquo;) hereby assigns, transfers and conveys
        to <strong>{c.creditorName || "the Creditor"}</strong> (&ldquo;Assignee&rdquo;) all right, title and interest in
        and to negotiable instrument <strong>{noteRef(d)}</strong>, original face principal
        <strong> {fmtCurrency(d.faceValue)}</strong>, maturity value <strong>{fmtCurrency(maturity)}</strong>, maturing
        {fmtDate(d.maturityDate)}.
      </P>
      <table className="legal-table" style={{ width: "100%", fontSize: "10pt", margin: "10px 0" }}>
        <tbody>
          <tr><td style={{ width: "45%", fontWeight: 600 }}>Obligation Satisfied</td><td>{c.debtTypeLabel}{c.accountNumber ? ` · Acct ${c.accountNumber}` : ""}</td></tr>
          <tr><td style={{ fontWeight: 600 }}>Balance Satisfied</td><td>{fmtCurrency(c.currentBalance)}</td></tr>
          <tr><td style={{ fontWeight: 600 }}>Consideration / Settlement Value</td><td>{fmtCurrency(c.payoffAmount || c.currentBalance)}</td></tr>
          <tr><td style={{ fontWeight: 600 }}>Effective Date</td><td>{fmtDate(c.effectiveDate)}</td></tr>
        </tbody>
      </table>
      <P>
        This assignment is made with full recourse to the instrument and without personal recourse to the Assignor.
        Upon acceptance, the Assignee accepts the instrument in full accord and satisfaction of the obligation and shall
        release all related liens and security interests. Governed by the laws of {d.borrowerState || "Tennessee"} and
        the Uniform Commercial Code.
      </P>
    </Paper>
  );
});

// ===========================================================================
// Underlying instruments + supporting agreements directed to the creditor
// ===========================================================================

// ---- CR-00A — Promissory Note (the Subject Note) ----
export const DocCR00PromissoryNote = React.forwardRef(({ d }, ref) => {
  const { maturity } = calc(d);
  return (
    <Paper ref={ref} footer={<DocFooter label="Doc CR-00A — Promissory Note (Subject Note)" />}>
      <DocHead tag="Negotiable Instrument · UCC Article 3" title="Promissory Note"
        sub={`${noteRef(d)} · Subject Note held by ${d.trustName || "Treasury House Trust"}`} />
      <P>
        FOR VALUE RECEIVED, the undersigned Maker, <strong>{d.makerName || d.borrowerName || "the Maker"}</strong>, hereby
        unconditionally promises to pay to the order of <strong>{d.trustName || "TREASURY HOUSE TRUST"}</strong> (the
        &ldquo;Holder&rdquo;), the principal sum of <strong>{fmtCurrency(d.faceValue)}</strong>, together with interest
        as set forth below, payable in lawful money of the United States.
      </P>
      <table className="legal-table" style={{ width: "100%", fontSize: "10pt", margin: "10px 0" }}>
        <tbody>
          <tr><td style={{ width: "45%", fontWeight: 600 }}>Principal Face Amount</td><td>{fmtCurrency(d.faceValue)}</td></tr>
          <tr><td style={{ fontWeight: 600 }}>Stated Rate</td><td>{d.couponRate ? `${d.couponRate}%` : "Zero-coupon (issued at discount)"}</td></tr>
          <tr><td style={{ fontWeight: 600 }}>Issue / Execution Date</td><td>{fmtDate(d.executionDate)}</td></tr>
          <tr><td style={{ fontWeight: 600 }}>Maturity Date</td><td>{fmtDate(d.maturityDate)}</td></tr>
          <tr><td style={{ fontWeight: 600 }}>Maturity Value Payable</td><td>{fmtCurrency(maturity)}</td></tr>
        </tbody>
      </table>
      <SectionTitle>Terms</SectionTitle>
      <P>
        This Note is a negotiable instrument under UCC Article 3, payable to order and freely transferable by
        endorsement and delivery. The Maker waives presentment, demand, protest and notice of dishonor. This Note is the
        Subject Note now directed by the Holder to the Creditor named in the accompanying instruments.
      </P>
      <div style={{ marginTop: 22, borderTop: "1.5px solid #0A0A0A", paddingTop: 14 }}>
        <div style={{ fontSize: "8.5pt", letterSpacing: "0.16em", textTransform: "uppercase", color: "#5C5C5C", marginBottom: 12 }}>Maker</div>
        <SignatureLine sig={findSig(d.signatures, "maker")} defaultName={d.makerName || d.borrowerName} />
        <div className="sig-line" style={{ marginTop: 4 }}>{d.makerName || d.borrowerName}{d.makerTitle ? `, ${d.makerTitle}` : ""}</div>
      </div>
    </Paper>
  );
});

// ---- CR-00B — Private Negotiable Instrument Draft ----
export const DocCR00BankersAcceptance = React.forwardRef(({ d }, ref) => {
  const baRef = makeSerial("BA-" + (d.trustName || "THT").replace(/\s/g, "").slice(0, 6).toUpperCase());
  return (
    <Paper ref={ref} footer={<DocFooter label="Doc CR-00B — Private Negotiable Instrument" />}>
      <DocHead tag="Private Negotiable Instrument · UCC §§ 3-409 / 3-410" title="Private Negotiable Instrument"
        sub={`${baRef} · Accepted & Payable at Maturity`} />
      <P>
        At maturity on <strong>{fmtDate(d.maturityDate)}</strong>, pay to the order of
        <strong> {d.trustName || "TREASURY HOUSE TRUST"}</strong> the sum of <strong>{fmtCurrency(d.faceValue)}</strong>
        in lawful money of the United States, drawn against and secured by the Subject Note {noteRef(d)}.
      </P>
      <div style={{ border: "1.5px solid #0A0A0A", padding: "12px 14px", margin: "14px 0", background: "#FBFBF7" }}>
        <div style={{ fontSize: "9pt", letterSpacing: "0.16em", textTransform: "uppercase", color: "#5C5C5C", marginBottom: 6 }}>Acceptance</div>
        <P>
          Accepted by the undersigned Accepting Bank, payable at maturity, against presentation of {noteRef(d)}. This
          Acceptance constitutes a primary, unconditional obligation of the Accepting Bank under UCC §§ 3-409 and 3-410
          and is eligible for discount in the secondary market.
        </P>
        <table className="legal-table" style={{ width: "100%", fontSize: "10pt" }}>
          <tbody>
            <tr><td style={{ width: "40%", fontWeight: 600 }}>Accepting Bank</td><td>{d.acceptanceBankName || "[ To be accepted — Accepting Bank ]"}</td></tr>
            <tr><td style={{ fontWeight: 600 }}>Accepting Officer</td><td>{d.acceptanceOfficer || "________________________"}</td></tr>
            <tr><td style={{ fontWeight: 600 }}>Date of Acceptance</td><td>{d.acceptanceBankName ? fmtDate(d.executionDate) : "________________"}</td></tr>
          </tbody>
        </table>
      </div>
      <TrustExecution d={d} />
    </Paper>
  );
});

// ---- CR-00C — Allonge / Endorsement ----
export const DocCR00Allonge = React.forwardRef(({ d }, ref) => {
  const c = cd(d);
  const endorsement = c.purpose === "collateral" || c.purpose === "finance" || c.purpose === "refinance"
    ? "as collateral security (pledged), without recourse,"
    : "without recourse,";
  return (
    <Paper ref={ref} footer={<DocFooter label="Doc CR-00C — Allonge / Endorsement" />}>
      <DocHead tag="Allonge · Affixed to the Subject Note" title="Allonge & Endorsement"
        sub={`Transfer / pledge of ${noteRef(d)} to ${c.creditorName || "the Creditor"}`} />
      <P>
        This Allonge is firmly affixed to and made a part of the Subject Note <strong>{noteRef(d)}</strong>, original face
        principal <strong>{fmtCurrency(d.faceValue)}</strong>, maturing {fmtDate(d.maturityDate)}.
      </P>
      <div style={{ border: "1px solid #0A0A0A", padding: "14px 16px", margin: "12px 0", fontSize: "11pt", lineHeight: 1.7 }}>
        PAY TO THE ORDER OF <strong>{c.creditorName || "________________________"}</strong>{c.creditorAddress ? `, ${c.creditorAddress}` : ""}, {endorsement} in connection with the {c.purposeLabel || "debt resolution"} of the obligation
        {c.accountNumber ? ` (Account/Loan No. ${c.accountNumber})` : ""}.
      </div>
      <P>
        TREASURY HOUSE TRUST, as Holder, hereby endorses and {endorsement.includes("collateral") ? "pledges" : "transfers"} the
        Subject Note to the above-named Creditor, by and through its Managing Trustee.
      </P>
      <TrustExecution d={d} />
    </Paper>
  );
});

// ---- CR-06 — Securities/Account Control & Acknowledgment of Pledge ----
export const DocCR06ControlPledgeAck = React.forwardRef(({ d }, ref) => {
  const c = cd(d);
  return (
    <Paper ref={ref} footer={<DocFooter label="Doc CR-06 — Control & Acknowledgment of Pledge" />}>
      <DocHead tag="UCC § 9-104 / § 9-106 · Control Agreement" title="Account Control & Acknowledgment of Pledge"
        sub={`Perfection by control · ${c.creditorName || "Secured Party"}`} />
      <P>
        This Agreement is entered among <strong>{d.trustName || "TREASURY HOUSE TRUST"}</strong> (the &ldquo;Pledgor&rdquo;),
        <strong> {c.creditorName || "the Secured Party"}</strong> (the &ldquo;Secured Party&rdquo;), and the holding
        institution, to establish the Secured Party&rsquo;s control over the pledged Subject Note {noteRef(d)} and its
        proceeds, perfecting the security interest by control under UCC §§ 9-104 and 9-106.
      </P>
      <SectionTitle>Acknowledgment</SectionTitle>
      <P>
        The holding institution acknowledges the Secured Party&rsquo;s security interest and agrees to comply with the
        Secured Party&rsquo;s entitlement orders and instructions concerning the Collateral without further consent of
        the Pledgor following a notice of exclusive control. The Collateral secures the <strong>{c.debtTypeLabel}</strong>
        {c.accountNumber ? ` (Acct ${c.accountNumber})` : ""}, balance {fmtCurrency(c.currentBalance)}.
      </P>
      <TrustExecution d={d} />
    </Paper>
  );
});

// ---- CR-07 — Mutual Release & Accord-and-Satisfaction Agreement ----
export const DocCR07MutualRelease = React.forwardRef(({ d }, ref) => {
  const c = cd(d);
  return (
    <Paper ref={ref} footer={<DocFooter label="Doc CR-07 — Mutual Release & Accord-and-Satisfaction" />}>
      <DocHead tag="Accord & Satisfaction · UCC § 3-311" title="Mutual Release & Accord-and-Satisfaction"
        sub={`Full and final ${c.purpose === "debt_swap" ? "exchange" : "settlement"} of the obligation`} />
      <P>
        This Agreement is made between <strong>{d.trustName || "TREASURY HOUSE TRUST"}</strong> (&ldquo;Holder&rdquo;) and
        <strong> {c.creditorName || "the Creditor"}</strong> (&ldquo;Creditor&rdquo;), to fully and finally
        {c.purpose === "debt_swap" ? " exchange/swap" : " settle"} the <strong>{c.debtTypeLabel}</strong>
        {c.accountNumber ? ` (Account/Loan No. ${c.accountNumber})` : ""}, current balance {fmtCurrency(c.currentBalance)}.
      </P>
      <SectionTitle>Consideration</SectionTitle>
      <P>
        In consideration of the tender and assignment of the Subject Note {noteRef(d)} (face principal {fmtCurrency(d.faceValue)})
        as accord, the Creditor accepts the same in full satisfaction of the obligation, valued at
        {" "}{fmtCurrency(c.payoffAmount || c.currentBalance)}.
      </P>
      <SectionTitle>Mutual Release</SectionTitle>
      <P>
        Upon performance, each party releases and forever discharges the other from all claims, demands and liabilities
        arising from or relating to the obligation, and the Creditor shall release all related liens and deliver a
        satisfaction/release to the Holder within ten (10) business days.
      </P>
      <TrustExecution d={d} />
    </Paper>
  );
});

// ---- CR-08 — Payoff & Lien Release Acknowledgment ----
export const DocCR08PayoffLienRelease = React.forwardRef(({ d }, ref) => {
  const c = cd(d);
  return (
    <Paper ref={ref} footer={<DocFooter label="Doc CR-08 — Payoff & Lien Release Acknowledgment" />}>
      <DocHead tag="Payoff Confirmation · Lien Release" title="Payoff & Lien Release Acknowledgment"
        sub={`${c.debtTypeLabel || "Debt Obligation"}${c.accountNumber ? ` · Acct ${c.accountNumber}` : ""}`} />
      <P>
        Upon application of the proceeds of the Subject Note {noteRef(d)} tendered under the accompanying Payoff Tender
        Letter, <strong>{c.creditorName || "the Creditor"}</strong>{c.servicer ? ` (Servicer: ${c.servicer})` : ""}
        {" "}acknowledges and confirms that the obligation described below is PAID IN FULL / SETTLED, and agrees to release
        all related liens and security interests.
      </P>
      <table className="legal-table" style={{ width: "100%", fontSize: "10pt", margin: "10px 0" }}>
        <tbody>
          <tr><td style={{ width: "50%", fontWeight: 600 }}>Obligation</td><td>{c.debtTypeLabel}{c.accountNumber ? ` · Acct ${c.accountNumber}` : ""}</td></tr>
          <tr><td style={{ fontWeight: 600 }}>Balance Satisfied</td><td>{fmtCurrency(c.currentBalance)}</td></tr>
          <tr><td style={{ fontWeight: 600 }}>Amount Applied</td><td>{fmtCurrency(c.payoffAmount || c.currentBalance)}</td></tr>
          <tr><td style={{ fontWeight: 600 }}>Effective Date</td><td>{fmtDate(c.effectiveDate)}</td></tr>
        </tbody>
      </table>
      <P>
        The Creditor shall deliver a recordable satisfaction/release and return any original instruments marked
        &ldquo;PAID&rdquo; to the Holder within ten (10) business days of the effective date.
      </P>
      <div style={{ marginTop: 22, borderTop: "1.5px solid #0A0A0A", paddingTop: 14 }}>
        <div style={{ fontSize: "8.5pt", letterSpacing: "0.16em", textTransform: "uppercase", color: "#5C5C5C", marginBottom: 12 }}>Acknowledged — Creditor</div>
        <div className="sig-line">By: {c.creditorName || "________________________"}</div>
        <div style={{ fontSize: "9pt", color: "#5C5C5C", marginTop: 2 }}>Authorized Officer · {c.servicer || c.creditorName}</div>
      </div>
      <TrustExecution d={d} />
    </Paper>
  );
});


// ---- CR-09 — Closing & Satisfaction Certificate (collateral free & clear) ----
// Certifies, in lieu of a binder closing, that the underlying obligation (auto
// loan / mortgage / other) has been fully satisfied and that the real-estate or
// vehicle collateral is released of all liens and obligations.
export const DocCR09SatisfactionCertificate = React.forwardRef(({ d }, ref) => {
  const c = cd(d);
  const isFinance = ["finance", "refinance", "collateral"].includes(c.purpose);
  const actionWord = c.purpose === "debt_swap" ? "exchanged and discharged"
    : isFinance ? "refinanced and restructured" : "satisfied and discharged in full";
  return (
    <Paper ref={ref} footer={<DocFooter label="Doc CR-09 — Closing & Satisfaction Certificate (Collateral Free & Clear)" />}>
      <DocHead tag="Closing Certificate · Satisfaction of Obligation · Lien Release"
        title="Closing & Satisfaction Certificate"
        sub={`${c.purposeLabel || "Debt Resolution"} · ${c.debtTypeLabel || "Debt Obligation"}`} />
      <P>
        This Closing &amp; Satisfaction Certificate (this &ldquo;Certificate&rdquo;) is executed and delivered as of
        {" "}<strong>{fmtDate(c.effectiveDate)}</strong> by <strong>{d.trustName || "TREASURY HOUSE TRUST"}</strong>
        {" "}(the &ldquo;Holder&rdquo;), by and through its Managing Trustee, and acknowledged by
        {" "}<strong>{c.creditorName || "the Creditor / Lender"}</strong>{c.servicer ? ` (Servicer: ${c.servicer})` : ""}
        {" "}(the &ldquo;Creditor&rdquo;), to evidence the closing and {actionWord} of the obligation described below
        and the release of the related collateral.
      </P>

      <SectionTitle>Obligation &amp; Collateral</SectionTitle>
      <table className="legal-table" style={{ width: "100%", fontSize: "10pt", margin: "10px 0" }}>
        <tbody>
          <tr><td style={{ width: "42%", fontWeight: 600 }}>Obligation / Debt Type</td><td>{c.debtTypeLabel || "Debt Obligation"}</td></tr>
          <tr><td style={{ fontWeight: 600 }}>Account / Loan No.</td><td>{c.accountNumber || "—"}</td></tr>
          <tr><td style={{ fontWeight: 600 }}>Creditor / Lender</td><td>{c.creditorName || "—"}</td></tr>
          <tr><td style={{ fontWeight: 600 }}>Collateral Released</td><td>{c.collateralDescription || "The real property / motor vehicle securing the above obligation"}</td></tr>
          <tr><td style={{ fontWeight: 600 }}>Effective Closing Date</td><td>{fmtDate(c.effectiveDate)}</td></tr>
        </tbody>
      </table>

      <SectionTitle>Closing Statement — Sources &amp; Application</SectionTitle>
      <table className="legal-table" style={{ width: "100%", fontSize: "10pt", margin: "10px 0" }}>
        <tbody>
          <tr><td style={{ width: "60%", fontWeight: 600 }}>Outstanding Balance Prior to Closing</td><td>{fmtCurrency(c.currentBalance)}</td></tr>
          <tr><td style={{ fontWeight: 600 }}>Instrument Tendered (Subject Note {noteRef(d)})</td><td>{fmtCurrency(d.faceValue)}</td></tr>
          <tr><td style={{ fontWeight: 600 }}>Amount Applied to Obligation</td><td>{fmtCurrency(c.payoffAmount || c.currentBalance)}</td></tr>
          <tr><td style={{ fontWeight: 600 }}>Remaining Balance After Closing</td><td><strong>{fmtCurrency(Math.max(0, (c.currentBalance || 0) - (c.payoffAmount || c.currentBalance || 0)))}</strong></td></tr>
        </tbody>
      </table>

      <SectionTitle>Certifications</SectionTitle>
      <P>
        The Creditor hereby <strong>CERTIFIES, ACKNOWLEDGES, AND AGREES</strong> that, upon the closing evidenced hereby:
      </P>
      <ol style={{ fontSize: "10pt", lineHeight: 1.6, paddingLeft: 20, margin: "0 0 10px" }}>
        <li>the above-described obligation is <strong>PAID, {actionWord.toUpperCase()}</strong>, and no further sums are or shall become due thereunder;</li>
        <li>all liens, mortgages, deeds of trust, security interests, UCC-1 financing statements, and certificate-of-title liens encumbering the Collateral are <strong>fully released and discharged</strong>;</li>
        <li>title to the Collateral is, or shall be, <strong>free and clear of all encumbrances</strong> in favor of the Creditor, and the Creditor shall reconvey clear title and deliver a recordable satisfaction / lien release (and, for a motor vehicle, the title marked &ldquo;lien released&rdquo;) to the Holder within ten (10) business days; and</li>
        <li>this Certificate may be recorded and relied upon by any title company, motor-vehicle department, county recorder, or third party as conclusive evidence of satisfaction and release.</li>
      </ol>

      <div style={{ marginTop: 22, borderTop: "1.5px solid #0A0A0A", paddingTop: 14 }}>
        <div style={{ fontSize: "8.5pt", letterSpacing: "0.16em", textTransform: "uppercase", color: "#5C5C5C", marginBottom: 12 }}>Certified &amp; Acknowledged — Creditor / Lender</div>
        <SignatureLine sig={findSig(d.signatures, "creditor")} defaultName={c.creditorName} />
        <div className="sig-line" style={{ marginTop: 4 }}>By: {c.creditorContact || "Authorized Officer"} · {c.creditorName || "Creditor"}</div>
        <div className="sig-line">Date: {fmtDate(c.effectiveDate)}</div>
      </div>

      {d.secureTransmission && (
        <div style={{ marginTop: 18, border: "1.5px solid #0B6B4F", borderRadius: 3, padding: "12px 14px", background: "#0B6B4F08" }}>
          <div style={{ fontSize: "8.5pt", letterSpacing: "0.16em", textTransform: "uppercase", color: "#0B6B4F", fontWeight: 700, marginBottom: 8 }}>
            Secure Transmission Evidence · Encrypted Data Exchange
          </div>
          <p style={{ fontSize: "9pt", lineHeight: 1.6, margin: "0 0 8px", color: "#1A1A1A" }}>
            The executed closing binder was transmitted to the Creditor / counterparty over an encrypted channel.
            The cryptographic fingerprint below is conclusive evidence of the exact document delivered and its integrity in transit.
          </p>
          <table className="legal-table" style={{ width: "100%", fontSize: "9.5pt", margin: "4px 0" }}>
            <tbody>
              <tr><td style={{ width: "42%", fontWeight: 600 }}>Binder SHA-256 (integrity hash)</td><td style={{ fontFamily: "monospace", wordBreak: "break-all" }}>{d.secureTransmission.hash || "—"}</td></tr>
              <tr><td style={{ fontWeight: 600 }}>Transport Security</td><td>{d.secureTransmission.mtls ? "Mutual TLS (client certificate) + HMAC-SHA256 signature" : "TLS + HMAC-SHA256 signature"}</td></tr>
              <tr><td style={{ fontWeight: 600 }}>Delivery Status</td><td>{d.secureTransmission.ok ? `Delivered & acknowledged${d.secureTransmission.statusCode ? ` (HTTP ${d.secureTransmission.statusCode})` : ""}` : "Transmission not confirmed"}</td></tr>
              <tr><td style={{ fontWeight: 600 }}>Transmitted At (UTC)</td><td>{d.secureTransmission.transmittedAt || "—"}</td></tr>
              {d.secureTransmission.webhookUrl && (
                <tr><td style={{ fontWeight: 600 }}>Counterparty Endpoint</td><td style={{ wordBreak: "break-all" }}>{d.secureTransmission.webhookUrl}</td></tr>
              )}
            </tbody>
          </table>
          <p style={{ fontSize: "8pt", lineHeight: 1.5, color: "#5C5C5C", margin: "6px 0 0" }}>
            The payload was encrypted at rest (AES-256), delivered via a short-lived presigned link, and signed end-to-end
            with HMAC-SHA256. Any alteration of the delivered binder would change the SHA-256 fingerprint above.
          </p>
        </div>
      )}

      <TrustExecution d={d} />
    </Paper>
  );
});
